import type { Proto } from "./Proto";
import type { EventEmitter, ModelSignal, OutputEmitterRef, Signal, Type, WritableSignal } from "@angular/core";
type Primitive = Proto.Primitive;

// Loose autocomplete auto-completes string values while still allowing all strings
// Particularly useful when you know some possible values, but not all
export type LooseAutocomplete<T extends string> = T | Omit<string, T>;

// Branding is a way to add more information to a generic type. Still compatible with the generic type, but incompatible with other branded types
interface Branding<BrandT> {
    _type: BrandT;
}
export type Brand<T, BrandT> = T & Branding<BrandT>;

// Flavoring is a way to give more information to a generic type. Unlike branding, flavoring allows inference and is mainly for developer information
export type Flavor<T, FlavorT> = T & Partial<Branding<FlavorT>>;

// Extracts the type of the values of an object
export type ValueOf<T> = T[keyof T];

// Extract all possible keys from a union of objects
export type KeysOfUnion<T> = T extends T ? keyof T : never;

// Make certain properties optional
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

// Make all descendent properties optional
export type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;

// Make certain properties required
export type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

// Omit keys from all descendent objects
export type DeepOmit<T, Y extends PropertyKey> = T extends Primitive
    ? T // Leave primitives be
    : T extends any[]
      ? { [P in keyof T]: DeepOmit<T[P], Y> } // Omit from members of arrays
      : T extends readonly any[]
        ? Readonly<{ [P in keyof T]: DeepOmit<T[P], Y> }> // And readonly array
        : T extends object
          ? { [P in Exclude<keyof T, Y>]: DeepOmit<T[P], Y> } // Omit from objects
          : never;

// Recursive array
export type NestedArray<T> = (T | NestedArray<T>)[];

// Help type for when optional parameter is not supplied
export type ValueElseUndefined<T> = T extends string | number | boolean | symbol | object ? T : undefined;

// If there's just one item, it's an object, if there's multiple, it's an array
// Anything that you would typically use S25Util.array.forceArray(T) on
export type MaybeArray<T> = T | T[];

// Unwrap MaybeArray<T> to T[]
export type DefinitelyArray<T> = [T] extends [undefined] ? unknown[] : T extends any[] ? T : T[];

// Unwrap MaybeArray<T> to T
export type UnwrapMaybeArray<T> = DefinitelyArray<T>[0];

// Picks properties which have a specified type
export type PickByType<T, Value> = {
    [P in keyof T as T[P] extends Value | undefined ? P : never]: T[P];
};

// Takes two types A and B and merges them such that properties of B will override A
export type Merge<A, B> = Omit<A, keyof B> & B;

// Unprefixes a string type
export type Unprefix<S extends string, Prefix extends string> = S extends `${Prefix}${infer Rest}` ? Rest : never;

// Omits any prop starting with the specified string
export type OmitByPrefix<Stack, Needle extends string> = Omit<Stack, `${Needle}${string}`>;

export type OmitPrivate<T> = OmitByPrefix<T, "_">;

// Some service return {nil: true} when there's no value?
export type MaybeNil<T> = T | { nil: true };

// Extracts keys from a map
export type KeyOfMap<M extends Map<unknown, unknown>> = M extends Map<infer K, unknown> ? K : never;

// Extract item type from set
export type SetItem<S> = S extends Set<infer Item> ? Item : never;

// Makes all properties writable signals
export type WritableSignals<T> = T extends object ? { [P in keyof T]: WritableSignal<T[P]> } : T;

// Makes all properties non-writable signals
export type Signals<T> = T extends object ? { [P in keyof T]: Signal<T[P]> } : T;

// Extract type from signal
export type InferSignal<S extends Signal<unknown>> = S extends Signal<infer T> ? T : never;

// Allows null and undefined for the type
export type Nullable<T> = T | null | undefined;

export type Refreshable = { refresh: () => void | Promise<void> };

// Extract type from "Type<T>". Feel like this should be built into Angular?
export type TypeFromType<T> = T extends Type<infer U> ? U : never;

// Extract a specific property from a signal store
export type SignalStoreProp<T extends Type<any>, Prop extends PropertyKey> = TypeFromType<T>[Prop];

// Infer the value type of a signal store signal
export type InferSignalProp<T extends Type<any>, Prop extends PropertyKey> = InferSignal<TypeFromType<T>[Prop]>;

/**
 * Returns a function which ensures that the key of the object matches the specified property
 * @example
 * const matchId = matchKeyAndProp<"id">("id");
 * const data = matchId({
 *   home: { id: 'home' },
 *   about: { id: 'about' },
 *   // error: Type '"wrong"' is not assignable to type '"contact"'
 *   contact: { id: 'wrong' }
 * });
 * @param prop
 */
export function matchKeyAndProp<Prop extends string>(prop: Prop) {
    return <Obj extends { [K in keyof Obj]: { [P in Prop]: K } }>(obj: Obj): Obj => {
        return obj;
    };
}

// Prefixes keys of objects with a provided string. Optionally recursive
export type PrefixKeys<
    Prefix extends string,
    T extends any,
    Deep extends boolean = false,
> = T extends readonly (infer U)[]
    ? Deep extends true
        ? PrefixKeys<Prefix, U, Deep>[]
        : U[]
    : T extends object
      ? {
            [K in keyof T as K extends string ? `${Prefix}${K}` : K]: Deep extends true
                ? PrefixKeys<Prefix, T[K], Deep>
                : T[K];
        }
      : T;

export type InferStore<Store extends Type<any>, Instance = InstanceType<Store>> = {
    [K in keyof Instance]: Instance[K] extends Signal<any> ? InferSignal<Instance[K]> : Instance[K];
};

// Extracts an array of entries from an object
export type Entries<T extends object> = { [K in keyof T]: [K, T[K]] }[keyof T][];

// Get input properties of a component
export type InputsOf<C extends Type<any>> = {
    [K in keyof InstanceType<C> as InstanceType<C>[K] extends EventEmitter<any> | OutputEmitterRef<any>
        ? never
        : K]?: InstanceType<C>[K] extends Signal<infer S>
        ? S | Signal<S>
        : InstanceType<C>[K] | Signal<InstanceType<C>[K]>;
};

// Get model properties of a component
export type ModelsOf<C extends Type<unknown>> = {
    // Only include keys that are model signals or have a corresponding "Change" signal
    [K in keyof InstanceType<C> as InstanceType<C>[K] extends ModelSignal<any>
        ? K
        : `${K & string}Change` extends keyof InstanceType<C>
          ? K
          : never]?: InstanceType<C>[K] extends ModelSignal<infer S>
        ? WritableSignal<S>
        : `${K & string}Change` extends keyof InstanceType<C>
          ? InstanceType<C>[`${K & string}Change`] extends EventEmitter<any> | OutputEmitterRef<any>
              ? WritableSignal<InstanceType<C>[K]>
              : never
          : never;
};

// Get output properties of a component
export type OutputsOf<C extends Type<unknown>> = {
    [K in keyof InstanceType<C> as InstanceType<C>[K] extends EventEmitter<any> | OutputEmitterRef<any>
        ? K
        : InstanceType<C>[K] extends ModelSignal<any>
          ? K extends string
              ? `${K}Change`
              : never
          : never]?: (
        event: InstanceType<C>[K] extends
            | EventEmitter<infer Output>
            | OutputEmitterRef<infer Output>
            | ModelSignal<infer Output>
            ? Output
            : never,
    ) => void | Promise<void>;
};
